import { Container, Graphics, Sprite, Texture, type Renderer } from 'pixi.js';
import { VEHICLE_LENGTH, VEHICLE_WIDTH, project, type Projection } from '@hotlist/shared';
import { CIVILIAN_COLORS, PATROL_COLOR, PLAYER_COLOR, SELECTED_COLOR, SELF_COLOR } from './theme.ts';
import type { Renderable } from './state.ts';
import type { Viewport } from './viewport.ts';

/**
 * Traffic.
 *
 * Vehicles are drawn in screen space rather than inside the zoomed map
 * container. Two reasons: a car at county zoom is a sixth of a pixel and has
 * to be given a floor, and culling to the visible rectangle is a comparison
 * that is already being done here anyway.
 *
 * This layer takes a flat list of already-interpolated positions and knows
 * nothing about where they came from. In solo mode they are lerped from the
 * local simulation; in live mode they are lerped between the last two
 * snapshots the server sent. The difference belongs upstream of here.
 */
export class VehicleLayer {
  readonly container = new Container();

  private readonly sprites = new Map<number, Sprite>();
  private readonly texture: Texture;
  private readonly patrolTexture: Texture;
  private readonly lightTexture: Texture;
  private readonly ringTexture: Texture;
  private ring: Sprite | null = null;
  /** Lit bars, keyed by vehicle, so only responding units carry the cost. */
  private readonly bars = new Map<number, Sprite>();

  /** Smallest a car is allowed to get, in pixels of length. */
  /**
   * How small a car is allowed to get.
   *
   * A car is 4.6 m long, which at county zoom is a third of a pixel, so there
   * has to be a floor or traffic is invisible. This was 3.4 px, which is a
   * speck: you could see that something was there and not which way it was
   * pointing, and the nose that tells you was a fifth of that.
   *
   * Ten, after six and then eight both turned out to still be specks.
   *
   * The floor that mattered more turned out to be the *width* one, which was
   * 55% of this and left a car three and a half pixels across -- a real car is
   * 41% as wide as it is long, and holding to that proportion at a size where
   * the whole thing is ten pixels means holding to a shape nobody can see. It
   * is exaggerated to 68% at the floor, and the exaggeration goes away on its
   * own as soon as the real dimensions are larger than the floor.
   */
  private readonly minPixels = 10;

  private readonly projection: Projection;

  constructor(renderer: Renderer, projection: Projection) {
    this.projection = projection;
    // One white shape, tinted per vehicle, so every car is one batched quad.
    /**
     * A car, with a dark casing around it.
     *
     * The casing is the fix for a real legibility problem: civilian paint is
     * deliberately muted -- §4 does not want anybody picked out by color -- and
     * the roads are drawn in the same pale blue-grey family, so a small pale car
     * on a pale road had almost no edge. Over aerial imagery it was worse
     * still, because the ground can be any color at all.
     *
     * Baked into the texture rather than added as a second sprite, and drawn
     * near-black so that the per-vehicle tint multiplies it to something still
     * near-black. One quad per car, and it reads on any background.
     *
     * **Thick on purpose.** The first version made it three texture pixels on
     * a seventy-pixel car, which is four percent -- so on a car drawn eight
     * pixels long the casing was a third of a pixel and simply was not there,
     * at exactly the zoom where it was needed. It is a quarter of the sprite
     * now, which still reads as an outline when a car is large and is a whole
     * pixel when it is small.
     */
    const shape = new Graphics()
      .roundRect(0, 0, 84, 46, 10)
      .fill(0x05070a)
      .roundRect(10, 10, 64, 26, 6)
      .fill(0xffffff)
      // A lighter nose, which is the only cue to which way a car is pointing
      // once it is four pixels long.
      .rect(58, 14, 14, 18)
      .fill(0xd8dee6);
    this.texture = renderer.generateTexture({ target: shape, resolution: 2 });
    shape.destroy();

    /**
     * A marked unit, in the livery everybody recognizes.
     *
     * A white car with black doors, which is what a Wisconsin squad car looks
     * like and what "black and white" means. Drawn as its own texture rather
     * than tinting the civilian one, because a tint multiplies the whole
     * sprite and two-tone is the entire point of the shape.
     */
    const marked = new Graphics()
      .roundRect(0, 0, 64, 26, 6)
      .fill(0xf2f5f8)
      // The door panel, which is the black half.
      .rect(18, 1, 28, 24)
      .fill(0x11151b)
      // And the light bar across the roof.
      .rect(28, 6, 7, 14)
      .fill(0x8a949f);
    this.patrolTexture = renderer.generateTexture({ target: marked, resolution: 2 });
    marked.destroy();

    // The bar, lit. Drawn as a separate sprite over the car so it can flash
    // without the car flashing with it.
    const bar = new Graphics().roundRect(0, 0, 20, 30, 6).fill(0xffffff);
    this.lightTexture = renderer.generateTexture({ target: bar, resolution: 2 });
    bar.destroy();

    /**
     * A ring under your own car.
     *
     * Green used to be the answer to "which one is mine", and it stopped being
     * available the moment signing on put you in a marked unit -- livery is
     * two specific colors and tinting it green throws away the thing that
     * makes it recognisable. So the "you" cue moves off the paintwork and
     * underneath, where it works for either car.
     */
    const ring = new Graphics().circle(24, 24, 22).stroke({ width: 5, color: 0xffffff });
    this.ringTexture = renderer.generateTexture({ target: ring, resolution: 2 });
    ring.destroy();
    this.container.sortableChildren = true;
  }

  /** Wall-clock milliseconds, for anything that has to blink. */
  private now = 0;

  update(items: readonly Renderable[], viewport: Viewport, selectedId: number | null): void {
    this.now = performance.now();
    const visible = viewport.visibleBounds(80);
    const scale = viewport.scale;

    const lengthPixels = Math.max(this.minPixels, VEHICLE_LENGTH * scale);
    const widthPixels = Math.max(this.minPixels * 0.68, VEHICLE_WIDTH * scale);
    const spriteScaleX = lengthPixels / 84;
    const spriteScaleY = widthPixels / 46;

    // Live ids in one pass, so retiring sprites below stays linear. Doing the
    // membership test per orphaned sprite instead would be quadratic in the
    // vehicle count every frame, which at a county's worth of traffic is the
    // difference between 60 fps and 6.
    const live = new Set<number>();
    for (const item of items) live.add(item.id);

    /**
     * Whether the ring got drawn on your car this frame.
     *
     * It used to be inferred from whether a self entity was in `items` at all,
     * which is not the same question and was wrong in the case that actually
     * happens: your car is in the list, the loop culls it for being outside the
     * view, and `continue` skips the line that moves the ring. The ring then
     * sat at whatever screen pixel it last occupied -- so panning or zooming
     * away from your own car left a green circle stranded somewhere unrelated,
     * still claiming to be you.
     */
    let ringed = false;

    for (const item of items) {
      const p = project(this.projection, item.x, item.y);

      if (p.sx < visible.minX || p.sx > visible.maxX || p.sy < visible.minY || p.sy > visible.maxY) {
        const existing = this.sprites.get(item.id);
        if (existing) existing.visible = false;
        // And everything drawn on top of it. A light bar left visible on a
        // culled car is the same stranded-marker bug as the ring below.
        const bar = this.bars.get(item.id);
        if (bar) bar.visible = false;
        continue;
      }

      let sprite = this.sprites.get(item.id);
      if (!sprite) {
        sprite = new Sprite(this.texture);
        sprite.anchor.set(0.5);
        this.container.addChild(sprite);
        this.sprites.set(item.id, sprite);
      }

      const selected = item.id === selectedId;
      // Your own car is the one you are looking for on a screen full of
      // traffic, and it is the only one whose position you are steering.
      const emphasis = item.self ? 2.4 : selected ? 1.7 : 1;

      const marked = item.kind === 'patrol';
      sprite.texture = marked ? this.patrolTexture : this.texture;
      sprite.visible = true;
      sprite.position.set(viewport.toScreenX(p.sx), viewport.toScreenY(p.sy));
      sprite.rotation = screenHeading(this.projection, item.heading);
      // A marked car is already two colors, so it is left alone unless it is
      // the one under the cursor -- tinting it would throw away the livery
      // that makes it recognisable in the first place.
      // A marked car is already two colors, so it is left alone -- including
      // when it is yours. The ring below says which one is yours instead.
      sprite.tint = marked
        ? 0xffffff
        : item.self
          ? SELF_COLOR
          : selected
            ? SELECTED_COLOR
            : colorFor(item);

      if (item.self) {
        if (!this.ring) {
          this.ring = new Sprite(this.ringTexture);
          this.ring.anchor.set(0.5);
          this.container.addChild(this.ring);
        }
        this.ring.visible = true;
        this.ring.position.set(sprite.position.x, sprite.position.y);
        this.ring.tint = SELF_COLOR;
        this.ring.alpha = 0.85;
        this.ring.zIndex = 1;
        const across = Math.max(lengthPixels * emphasis * 1.5, 16);
        this.ring.width = across;
        this.ring.height = across;
        ringed = true;
      }

      // Cherries and berries. Alternating rather than blended, because that is
      // what a light bar does and a purple average is what it looks like when
      // you blend them.
      // Lights, not livery. A player on shift in an unmarked car can be
      // running code, and a marked unit can be parked doing nothing.
      if (item.lights) {
        let bar = this.bars.get(item.id);
        if (!bar) {
          bar = new Sprite(this.lightTexture);
          bar.anchor.set(0.5);
          bar.blendMode = 'add';
          this.container.addChild(bar);
          this.bars.set(item.id, bar);
        }
        bar.visible = true;
        bar.position.set(sprite.position.x, sprite.position.y);
        bar.rotation = sprite.rotation;
        bar.scale.set(spriteScaleX * emphasis * 1.6, spriteScaleY * emphasis * 1.6);
        // Twice a second, which reads as flashing without becoming a strobe.
        bar.tint = Math.floor(this.now / 260) % 2 === 0 ? 0xff2f3a : 0x2f6bff;
        bar.zIndex = 3;
        bar.alpha = 0.85;
      } else {
        const bar = this.bars.get(item.id);
        if (bar) bar.visible = false;
      }
      sprite.scale.set(spriteScaleX * emphasis, spriteScaleY * emphasis);
      sprite.zIndex = item.self ? 2 : selected ? 1 : 0;
    }

    if (!ringed && this.ring) this.ring.visible = false;

    // Anything that left the simulation, or left this client's interest set.
    for (const [id, sprite] of this.sprites) {
      if (live.has(id)) continue;
      sprite.destroy();
      this.sprites.delete(id);
      this.bars.get(id)?.destroy();
      this.bars.delete(id);
    }
  }

  /**
   * The vehicle nearest a screen point within `radius` pixels, or null.
   * Used for click-to-select.
   */
  pick(
    items: readonly Renderable[],
    viewport: Viewport,
    px: number,
    py: number,
    /**
     * How near counts as a tap on it.
     *
     * Eighteen pixels is a mouse cursor, which lands where you point it. A
     * fingertip covers about forty and lands where it lands, so on touch this
     * is opened up by the caller -- picking a car out of traffic is hard enough
     * with the cars being small on purpose.
     */
    radius = 18,
  ): Renderable | null {
    let best: Renderable | null = null;
    let bestDistance = radius * radius;
    for (const item of items) {
      const p = project(this.projection, item.x, item.y);
      const dx = viewport.toScreenX(p.sx) - px;
      const dy = viewport.toScreenY(p.sy) - py;
      const distance = dx * dx + dy * dy;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = item;
      }
    }
    return best;
  }
}

function colorFor(item: Renderable): number {
  if (item.kind === 'patrol') return PATROL_COLOR;
  if (item.kind === 'player') return PLAYER_COLOR;
  return CIVILIAN_COLORS[item.id % CIVILIAN_COLORS.length] as number;
}

/**
 * A world heading, foreshortened. A car driving due north on a tilted map does
 * not point up the screen at the same angle it points across the ground, and
 * getting this wrong makes every vehicle look slightly sideways on the
 * north-south roads.
 */
function screenHeading(projection: Projection, heading: number): number {
  return Math.atan2(-Math.sin(heading) * projection.yScale, Math.cos(heading));
}
